import React, { useState, useEffect, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/firebase';
import './NotificationSettings.css';

const NotificationSettings = () => {
  const [user] = useAuthState(auth);
  const [settings, setSettings] = useState({
    workUploads: true,
    gpsCheckins: true,
    newSnags: true,
    snagReplies: true,
    documentProgress: true,
    documentCompleted: true,
    emailNotifications: false,
    pushNotifications: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const loadSettings = useCallback(async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.notificationSettings) {
          setSettings(prev => ({ ...prev, ...userData.notificationSettings }));
        }
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
    } finally {
      setLoading(false);
    }
  }, [user.uid]);

  useEffect(() => {
    if (user) {
      loadSettings();
    }
  }, [user, loadSettings]);

  const handleSettingChange = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const saveSettings = async () => {
    if (!user) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        notificationSettings: settings,
        settingsUpdatedAt: new Date()
      });

      // Stored on user doc; mobile (iOS/Android) push respects these where implemented.

      console.log('Notification settings saved successfully');
    } catch (error) {
      console.error('Error saving notification settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="notification-settings-loading">Loading settings...</div>;
  }

  return (
    <div className="notification-settings">
      <div className="setting-item">
        <div className="setting-info">
          <label>Mobile push notifications</label>
          <p>Receive push notifications on mobile devices (iOS/Android)</p>
        </div>
        <div className="setting-control">
          <input
            type="checkbox"
            checked={settings.pushNotifications}
            onChange={() => handleSettingChange('pushNotifications')}
          />
          <span className="status-text">Controls push notifications on mobile devices</span>
        </div>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Activity posts & work uploads</label>
          <p>When team members share photos, voice notes, or activity updates</p>
        </div>
        <input
          type="checkbox"
          checked={settings.workUploads}
          onChange={() => handleSettingChange('workUploads')}
        />
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>GPS Check-ins</label>
          <p>When team members check in/out at site</p>
        </div>
        <input
          type="checkbox"
          checked={settings.gpsCheckins}
          onChange={() => handleSettingChange('gpsCheckins')}
        />
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>New snags</label>
          <p>When new snags are reported</p>
        </div>
        <input
          type="checkbox"
          checked={settings.newSnags}
          onChange={() => handleSettingChange('newSnags')}
        />
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Snag replies</label>
          <p>When team members reply to snags</p>
        </div>
        <input
          type="checkbox"
          checked={settings.snagReplies}
          onChange={() => handleSettingChange('snagReplies')}
        />
      </div>
      
      <div className="setting-item">
        <div className="setting-info">
          <label>Document progress</label>
          <p>When someone signs a document (signing progress updates)</p>
        </div>
        <input
          type="checkbox"
          checked={settings.documentProgress}
          onChange={() => handleSettingChange('documentProgress')}
        />
      </div>
      
      <div className="setting-item">
        <div className="setting-info">
          <label>Document completed</label>
          <p>When a document is fully signed by all parties</p>
        </div>
        <input
          type="checkbox"
          checked={settings.documentCompleted}
          onChange={() => handleSettingChange('documentCompleted')}
        />
      </div>

      <div className="settings-actions">
        <button 
          className="save-button"
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save settings'}
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings; 